import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const COACH_TOKEN_KEY = "ccfl_coach_token";
export const LEADER_TOKEN_KEY = "ccfl_leader_token";

// ——— Per-session secrets (privacy: every walkthrough/thread is gated by a secret) ———
// Stored as { [id]: secret } maps. Returned once by the backend on creation, then
// replayed via the `X-Session-Secret` header on every follow-up request.
const WALKTHROUGH_SECRETS_KEY = "ccfl_walkthrough_secrets";
const THREAD_SECRETS_KEY = "ccfl_thread_secrets";
const NEEDS_SECRETS_KEY = "ccfl_needs_secrets";

function _readSecretMap(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}") || {};
  } catch {
    return {};
  }
}

function _writeSecretMap(key, map) {
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

export function rememberWalkthroughSecret(id, secret) {
  if (!id || !secret) return;
  const m = _readSecretMap(WALKTHROUGH_SECRETS_KEY);
  m[id] = secret;
  _writeSecretMap(WALKTHROUGH_SECRETS_KEY, m);
}

export function rememberThreadSecret(id, secret) {
  if (!id || !secret) return;
  const m = _readSecretMap(THREAD_SECRETS_KEY);
  m[id] = secret;
  _writeSecretMap(THREAD_SECRETS_KEY, m);
}

export function rememberNeedsSecret(id, secret) {
  if (!id || !secret) return;
  const m = _readSecretMap(NEEDS_SECRETS_KEY);
  m[id] = secret;
  _writeSecretMap(NEEDS_SECRETS_KEY, m);
}

export function forgetWalkthroughSecret(id) {
  if (!id) return;
  const m = _readSecretMap(WALKTHROUGH_SECRETS_KEY);
  delete m[id];
  _writeSecretMap(WALKTHROUGH_SECRETS_KEY, m);
}

// Extract a walkthrough or coach-thread id from a request URL (without the API prefix).
function _idFromWalkthroughUrl(url) {
  // matches /walkthrough/{id}, /walkthrough/{id}/turn, /walkthrough/{id}/plan, etc.
  const m = url.match(/^\/walkthrough\/([^/?]+)(?:\/.*)?$/);
  return m ? m[1] : null;
}

function _idFromThreadUrl(url) {
  // matches /coach/thread/{id}, /coach/thread/{id}/messages, /coach/thread/{id}/message, /coach/thread/{id}/file, etc.
  const m = url.match(/^\/coach\/thread\/([^/?]+)(?:\/.*)?$/);
  return m ? m[1] : null;
}

function _idFromNeedsUrl(url) {
  // matches /needs/{id}, /needs/{id}/advance, /needs/{id}/dig, /needs/{id}/report.pdf, etc.
  // Excludes /needs/start and /needs/vocabulary.
  const m = url.match(/^\/needs\/([^/?]+)(?:\/.*)?$/);
  if (!m) return null;
  if (m[1] === "start" || m[1] === "vocabulary") return null;
  return m[1];
}

export const api = axios.create({ baseURL: API });

const isCoachAdminRoute = (method, url) => {
  if (url.startsWith("/auth/coach")) return true;
  if (url.startsWith("/coach/dashboard")) return true;
  if (url.startsWith("/admin/")) return true;
  if (url === "/coach/availability/all") return true;
  // POST or DELETE /coach/availability[/{id}] are admin (the public GET /coach/availability is NOT)
  if (
    (method === "post" || method === "delete") &&
    (url === "/coach/availability" || url.startsWith("/coach/availability/"))
  ) {
    return true;
  }
  return false;
};

const isLeaderRoute = (url) =>
  url.startsWith("/auth/leader") || url.startsWith("/leader/");

api.interceptors.request.use((config) => {
  // Respect explicit Authorization headers
  if (config.headers && (config.headers.Authorization || config.headers.authorization)) {
    // still try to attach session secret below
  } else {
    const url = config.url || "";
    const method = (config.method || "get").toLowerCase();
    const coachToken = localStorage.getItem(COACH_TOKEN_KEY);
    const leaderToken = localStorage.getItem(LEADER_TOKEN_KEY);

    if (isCoachAdminRoute(method, url) && coachToken) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${coachToken}` };
    } else if (isLeaderRoute(url) && leaderToken) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${leaderToken}` };
    } else if (
      leaderToken &&
      (url === "/coach/thread" || url.startsWith("/coach/thread/")) &&
      !isCoachAdminRoute(method, url)
    ) {
      // Tag new threads with leader_id when a leader is signed in
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${leaderToken}` };
    } else if (
      leaderToken &&
      url.startsWith("/walkthrough/")
    ) {
      // Attach leader JWT to walkthrough reads so owner_leader_id is honored.
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${leaderToken}` };
    } else if (leaderToken && url.startsWith("/needs")) {
      // /needs requires leader auth; attach the JWT on every call.
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${leaderToken}` };
    }
  }

  // ——— Attach the per-session secret if we have one for this id ———
  const url = config.url || "";
  const wid = _idFromWalkthroughUrl(url);
  if (wid) {
    const secrets = _readSecretMap(WALKTHROUGH_SECRETS_KEY);
    if (secrets[wid]) {
      config.headers = { ...(config.headers || {}), "X-Session-Secret": secrets[wid] };
    }
  } else {
    const tid = _idFromThreadUrl(url);
    if (tid) {
      const secrets = _readSecretMap(THREAD_SECRETS_KEY);
      if (secrets[tid]) {
        config.headers = { ...(config.headers || {}), "X-Session-Secret": secrets[tid] };
      }
    } else {
      const nid = _idFromNeedsUrl(url);
      if (nid) {
        const secrets = _readSecretMap(NEEDS_SECRETS_KEY);
        if (secrets[nid]) {
          config.headers = { ...(config.headers || {}), "X-Session-Secret": secrets[nid] };
        }
      }
    }
  }

  return config;
});

// ——— Response interceptor: capture access_secret on creation, persist it ———
api.interceptors.response.use((response) => {
  try {
    const cfg = response.config || {};
    const url = cfg.url || "";
    const method = (cfg.method || "get").toLowerCase();
    const data = response.data || {};
    if (method === "post" && url === "/walkthrough/start" && data.id && data.access_secret) {
      rememberWalkthroughSecret(data.id, data.access_secret);
    }
    if (method === "post" && url === "/coach/thread" && data.id && data.access_secret) {
      rememberThreadSecret(data.id, data.access_secret);
    }
    if (method === "post" && url === "/needs/start" && data.id && data.access_secret) {
      rememberNeedsSecret(data.id, data.access_secret);
    }
  } catch {
    // never let secret bookkeeping break the response
  }
  return response;
});
